<script setup lang="ts">
import {
  computed,
  type CSSProperties,
  inject,
  nextTick,
  onMounted,
  ref,
  useAttrs,
  useTemplateRef,
  watch,
} from "vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
  type ControlSize,
} from "../models";

export type BcFileInputProps = {
  /** Disables file input for upload */
  disabled?: boolean;
  /** Specifies custom validation messages */
  errors?: Map<string, string>;
  /** Expands the file input to the width of its parent container */
  fullWidth?: boolean;
  /** Specifies a short hint (placeholder) before file upload */
  placeholder?: string | number | boolean;
  /** Expands the file input to the width of the placeholder */
  placeholderWidth?: boolean;
  /** Specifies that file input is read-only */
  readonly?: boolean;
  /** Specifies pre-defined sizes for the file input */
  size?: ControlSize;
};

export type BcFileInputEmits = {
  (event: "validate", payload: File | Array<File>): void;
};

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

/** Specifies two-way binding for the file input */
const modelValue = defineModel<File | Array<File>>({
  validator: (value) =>
    value instanceof File ||
    (Array.isArray(value) && value.every((v) => v instanceof File)),
});

const {
  disabled = false,
  errors = new Map<string, string>(),
  fullWidth = false,
  placeholder = undefined,
  placeholderWidth = false,
  readonly = false,
  size,
} = defineProps<BcFileInputProps>();

const emit = defineEmits<BcFileInputEmits>();

/** Specifies the file input validation message keys */
const errorKeys = ref<Array<string>>([]);
/** Specifies that the file input is filled out properly */
const validity = ref(true);

/** The file input reference */
const fileInputRef = useTemplateRef("fileInputRef");
/** The text input reference (for showing selected files) */
const textInputRef = useTemplateRef("textInputRef");

/** Computes the allowed extensions based on the `accept` attribute */
const extensions = computed<string[]>(() => {
  if (!attrs.accept) {
    return [];
  }

  return (attrs.accept as string)
    .trim()
    .replace(" ", "")
    .split(",")
    .filter((ext) => ext.startsWith("."));
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => attrs.style as CSSProperties);
const inheritedAttrs = computed(() => {
  const { class: _, style: __, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

/**
 * Checks whether the file input has any constraints and whether it satisfies them.
 * @returns The element satisfies its constraints.
 */
function checkValidity(): boolean {
  errorKeys.value = [];
  validity.value = true;
  if (fileInputRef.value) fileInputRef.value.title = "";

  if (disabled || readonly) {
    validity.value = true;
    return validity.value;
  }

  if (
    attrs.required &&
    (!textInputRef.value?.value || textInputRef.value?.value === "")
  ) {
    errorKeys.value.push("valueMissing");
    if (fileInputRef.value)
      fileInputRef.value.title = getValidationMessage("valueMissing");
    validity.value = false;
  } else if (attrs.accept) {
    if (
      getFileExtensions().some(
        (ext) =>
          typeof extensions.value.find((e) => e.toLowerCase() === ext.toLowerCase()) ===
          "undefined",
      )
    ) {
      errorKeys.value.push("badInput");
      if (fileInputRef.value) fileInputRef.value.title = getValidationMessage("badInput");
      validity.value = false;
    }
  }

  if (
    fileInputRef.value &&
    fileInputRef.value.files &&
    fileInputRef.value.files.length > 0
  ) {
    const file = fileInputRef.value.files.item(0);
    if (!attrs.multiple) {
      if (file) emit("validate", file);
    } else {
      emit("validate", Array.from(fileInputRef.value.files));
    }
  } else if (modelValue.value) {
    emit("validate", modelValue.value);
  }
  return validity.value ?? false;
}

/**
 * Gets the selected file(s) extension(s).
 * @returns {string[]} file extension list
 */
function getFileExtensions(): string[] {
  let extensions: string[] = [];
  if (fileInputRef.value?.files) {
    for (let i = 0; i < fileInputRef.value.files.length; i++) {
      const file = fileInputRef.value.files.item(i);
      if (file && file.name.lastIndexOf(".") !== -1) {
        extensions.push(file.name.slice(file.name.lastIndexOf(".")));
      }
    }
  } else if (modelValue.value) {
    if (
      modelValue.value instanceof File &&
      modelValue.value.name.lastIndexOf(".") !== -1
    ) {
      extensions.push(
        modelValue.value.name.slice(modelValue.value.name.lastIndexOf(".")),
      );
    } else if (Array.isArray(modelValue.value)) {
      modelValue.value.forEach((file) => {
        if (file && file.name.lastIndexOf(".") !== -1) {
          extensions.push(file.name.slice(file.name.lastIndexOf(".")));
        }
      });
    }
  }
  return extensions;
}

/** Gets the selected file names. */
function getFileNames(): string {
  let names = "";
  if (fileInputRef.value?.files && fileInputRef.value?.files.length > 0) {
    for (let i = 0; i < fileInputRef.value.files.length; i++) {
      const file = fileInputRef.value.files.item(i);
      if (file) {
        if (i < fileInputRef.value.files.length - 1) {
          names += `${file.name}, `;
        } else {
          names += file.name;
        }
      }
    }
  } else if (modelValue.value) {
    if (modelValue.value instanceof File) {
      return modelValue.value.name;
    } else if (Array.isArray(modelValue.value)) {
      if (!attrs.multiple && modelValue.value?.length > 0) {
        return modelValue.value[0].name;
      }
      modelValue.value.forEach((file, fileIdx, fileList) => {
        if (file) {
          if (fileIdx < fileList.length - 1) {
            names += `${file.name}, `;
          } else {
            names += file.name;
          }
        }
      });
    }
  }
  return names;
}

/**
 * Gets validation message based on error key
 * @param {string} errorKey error key
 * @param {string[]|number[]} replaceList list of strings to replace in text
 */
function getValidationMessage(
  errorKey: string,
  ...replaceList: Array<string | number>
): string {
  let error = errors.get(errorKey) ?? errorKey;
  replaceList.forEach((item, idx) => {
    error = error.replace(`{${idx}}`, item.toString());
  });
  return error;
}

/**
 * Updates the file input and emits change event.
 */
async function onFileChange(): Promise<void> {
  if (fileInputRef.value?.files) {
    if (!attrs.multiple) {
      modelValue.value = fileInputRef.value.files.item(0) ?? undefined;
    } else {
      modelValue.value = Array.from(fileInputRef.value.files);
    }
    await nextTick();
    if (textInputRef.value) textInputRef.value.value = getFileNames();
    checkValidity();
  }
  fileInputRef.value?.blur();
}

async function onValueChanged(value: File | Array<File> | undefined): Promise<void> {
  if (!fileInputRef.value) {
    return;
  }
  const dataTransfer = new DataTransfer();
  if (value instanceof File) {
    dataTransfer.items.add(value);
  } else if (Array.isArray(value)) {
    value.forEach((v) => dataTransfer.items.add(v));
  }
  fileInputRef.value.files = dataTransfer.files;
  await nextTick();
  if (textInputRef.value) textInputRef.value.value = getFileNames();
  checkValidity();
}

/** Watches the `errors` attribute */
function onErrorsChanged(): void {
  if (fileInputRef.value && errorKeys.value.length) {
    fileInputRef.value.title = errors.get(errorKeys.value[0]) ?? "";
  }
}

function onDisabledOrReadonlyChanged(value: boolean): void {
  if (fileInputRef.value) {
    fileInputRef.value.title =
      value && errorKeys.value.length ? "" : (errors.get(errorKeys.value[0]) ?? "");
  }
}

onMounted(async () => {
  if (textInputRef.value) {
    textInputRef.value.value = getFileNames();
    textInputRef.value.title = "";
  }
  if (fileInputRef.value) fileInputRef.value.title = "";

  await nextTick();
  if (!disabled && !readonly && modelValue.value) {
    checkValidity();
  }
});

watch(modelValue, onValueChanged);
watch(() => errors, onErrorsChanged, { deep: true });
watch(() => disabled, onDisabledOrReadonlyChanged);
watch(() => readonly, onDisabledOrReadonlyChanged);

defineExpose({
  attrs,
  modelValue,
  errorKeys,
  validity,
  fileInputRef,
  textInputRef,
  extensions,
  checkValidity,
  getValidationMessage,
});
</script>

<template>
  <div
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    class="bissantzControls fileInputControl"
    v-bind:class="[
      size ? `file-input-${size}` : '',
      {
        disabled: disabled,
        fullWidth: fullWidth,
        invalid: !validity,
        readonly: readonly,
      },
      ...rootClass,
    ]"
    v-bind:style="rootStyle"
  >
    <span v-if="placeholderWidth && placeholder" class="widthtestbox">{{
      placeholder
    }}</span>
    <div class="inputContainer">
      <input
        ref="textInputRef"
        type="text"
        readonly
        tabindex="-1"
        class="textInput"
        v-bind:placeholder="placeholder?.toString()"
        v-bind:disabled="disabled"
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 10 20"
        width="0.625em"
        height="100%"
      >
        <polygon points="10,5 0,5 5,0" stroke-width="1" />
        <line x1="5" y1="30" x2="5" y2="0" stroke-width="1" />
      </svg>
      <input
        ref="fileInputRef"
        class="fileInput"
        type="file"
        v-bind="inheritedAttrs"
        v-bind:disabled="readonly || disabled"
        v-on:change="onFileChange"
      />
    </div>
    <span v-if="!disabled && !validity && errorKeys.length" class="validationMessage">
      <span v-for="(errorKey, idx) in errorKeys" v-bind:key="idx">
        {{ getValidationMessage(errorKey) }}
      </span>
    </span>
  </div>
</template>

<style lang="less">
@import "../styles/components/bc-file-input.less";
</style>
